<script>
  import { onMount, createEventDispatcher } from "svelte";
  import RestPilots from "../components/RestPilots.svelte";
  import RestStripe from "../components/RestStripe.svelte";
  import RestTaxJar from "../components/RestTaxJar.svelte";
  import MyModal from "../components/MyModal.svelte";
  import { account, appSession, appConstants } from "../stores/app.js";
  import { loadStripe } from "@stripe/stripe-js";
  import StripeForm from "../components/StripeForm.svelte";

  let stripe = null;
  let restPilots = null;
  let restStripe = null;
  let restTaxJar = null;
  let myModal = null;
  let busy = true;
  let stripeConfig = null;
  let salesTax = 0;
  let taxRate = 0;
  let total = 0;

  const dispatch = createEventDispatcher();

  onMount(async () => {
    try {
      busy = true;

      // get stripe config
      stripeConfig = await restStripe.GetConfig();

      // load stripe
      stripe = await loadStripe(stripeConfig.clientkey);

      // update subscription and taxes
      await updateAmounts();

      busy = false;
    } catch (error) {
      busy = false;

      switch (error) {
        case "BadRole":
        case "BadRealm":
          appSession.set({ authorisationToken: null, pilotId: null });
          break;
        case "NetworkError":
          myModal.showAlert(
            "Network error",
            "A network error occurred.<p> Check network connection and try again.</p>"
          );
          break;
        default:
          let alertError = "<p>Sorry an internal error occurred</p>";
          myModal.showAlert("Error", alertError);
          break;
      }
    }
  });

  // update payment amounts
  async function updateAmounts() {
    // get postcode
    let pilot = await restPilots.GetPilots($appSession.pilotId);

    // get tax rate
    let taxes = await restTaxJar.PostRate({
      postcode: pilot.billing_postcode,
      state: pilot.billing_state,
      amount: stripeConfig.plans.pilot.price,
    });

    if (taxes.success) {
      taxRate = taxes.data.taxrate;
      if (taxRate > 0) salesTax = (taxRate / 100) * stripeConfig.plans.pilot.price;
      total = stripeConfig.plans.pilot.price + salesTax;
    } else throw "TaxRateError";
  }

  async function processCreditCard(event) {
    event.detail.event.preventDefault();
    if (!event.detail.cardComplete) return;

    try {
      busy = true;

      // get pilot details
      let pilot = await restPilots.GetPilots($appSession.pilotId);

      let customerId = pilot.stripe_customerid;
      if (customerId == "") throw "UndefinedStripeCustomerId";

      let paymentMethod = await stripe.createPaymentMethod({ type: "card", card: event.detail.cardElement });

      if (typeof paymentMethod.error !== "undefined") throw paymentMethod.error.message;

      let subscriptionOk = await restStripe.PostSubscription(
        paymentMethod.paymentMethod.id,
        stripeConfig.plans.pilot.priceid,
        taxRate
      );

      if (subscriptionOk == true) {
        account.set({
          ...$account,
          subscriptionRequired: false,
          subscriptionExpired: false,
        });
      }

      setTimeout(() => {
        M.toast({ html: "Thank you for subscribing!" }, { displayLength: 3000 });
      }, 1500);

      busy = false;
    } catch (error) {
      busy = false;

      switch (error) {
        case "BadRole":
        case "BadRealm":
          appSession.set({ authorisationToken: null, pilotId: null });
          break;
        case "NetworkError":
          myModal.showAlert(
            "Network error",
            "A network error occurred.<p> Check network connection and try again.</p>"
          );
          break;
        default:
          let alertError = "<p>Sorry the following error was generated while processing your subscription:</p>";
          alertError += "<p>" + error + "</p>";
          alertError += "<p>Please check your credit card details and try again.</p>";
          myModal.showAlert("Error", alertError);
          break;
      }
    }
  }
</script>

<style>
  table.payment-details {
    width: auto;
  }
  table.payment-details tr {
    border-bottom: none;
  }
</style>

<div class="container">
  <img
    src="assets/logo.png"
    style="display: block; max-width: 80%; height: auto; margin: 40px auto 40px auto;"
    alt="" />

  {#if stripeConfig}
    <p style="font-size: 2em; text-align: center;">Subscription Expired</p>

    <p>
      Your JumpHub subscription has expired. To restart your subscription please enter your credit card details below.
    </p>

    <p><b>Payment details</b></p>

    <table class="payment-details">
      <tr>
        <td>JumpHub Pilot Plan</td>
        <td style="width: 100px; text-align: right;">${stripeConfig.plans.pilot.price.toFixed(2)}</td>
      </tr>
      {#if salesTax > 0}
        <tr>
          <td>Sales Tax ({taxRate}%)</td>
          <td style="text-align: right;">${salesTax.toFixed(2)}</td>
        </tr>
      {/if}
      <tr>
        <td><b>Monthly charge</b></td>
        <td style="text-align: right; font-weight: bold;">${total.toFixed(2)}</td>
      </tr>
    </table>

    <p style="font-size: 0.85em;">Note: your JumpHub subscription can be canceled at any time.</p>

    {#if stripe}
      <div style="margin-top: 20px;">
        <StripeForm
          {stripe}
          goButtonText="Pay now"
          cancelButtonText="Sign-out"
          showCancel={true}
          disabled={busy}
          on:submit={(evt) => {
            processCreditCard(evt);
          }}
          on:cancel={() => {
            dispatch('logout', {});
          }} />
      </div>
    {/if}
  {/if}

  {#if busy}
    <div
      style="display: flex; justify-content: center; align-items: center; height: 50px; width: 100%; margin-top: 50px;">
      <div class="preloader-wrapper small active">
        <div class="spinner-layer spinner-green-only">
          <div class="circle-clipper left">
            <div class="circle" />
          </div>
          <div class="gap-patch">
            <div class="circle" />
          </div>
          <div class="circle-clipper right">
            <div class="circle" />
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<RestPilots bind:this={restPilots} />
<RestStripe bind:this={restStripe} />
<RestTaxJar bind:this={restTaxJar} />
<MyModal bind:this={myModal} />
